import * as Sentry from "@sentry/react"

Sentry.init({
  dsn: "https://12091bb5585a017e4855240b97a2a9ae@o235488.ingest.us.sentry.io/4508547607822336",
  environment: import.meta.env.PROD ? "production" : "development",
  tracesSampleRate: 1.0,
  enabled: import.meta.env.PROD,
})

import "./index.css"

import { QueryClient } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister"
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client"
import React from "react"
import ReactDOM from "react-dom/client"
import toast from "react-hot-toast"
import superjson from "superjson"

import App from "./App"
import { AuthProvider } from "./lib/auth-context"
import { CACHE_VERSION, MAX_CACHE_AGE, QUERY_CACHE_EXPIRY, SKIP_PERSIST_KEYS } from "./lib/cacheConfig"
import { ThemeProvider } from "./lib/theme-context"
import { createTRPCReactClient, trpc } from "./lib/trpc"

/**
 * Global error handler for tRPC/React Query errors
 * Shows verbose toast messages in development mode
 */
interface QueryErrorLike {
  message?: string
  code?: string
  status?: number
  path?: string
  data?: {
    code?: string
    httpStatus?: number
    path?: string
  }
}

const asQueryErrorLike = (error: unknown): QueryErrorLike => (typeof error === "object" && error !== null ? error : {})

const stringifyQueryKeyPart = (value: unknown) => {
  if (typeof value === "string") return value
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") return String(value)
  if (typeof value === "symbol") return value.description ?? "symbol"
  try {
    return JSON.stringify(value) ?? "undefined"
  } catch {
    return "unserializable"
  }
}

const handleGlobalError = (error: unknown) => {
  const queryError = asQueryErrorLike(error)
  // Extract error details
  const errorMessage = queryError.message || "An error occurred"
  const errorCode = queryError.data?.code || queryError.code || "UNKNOWN"
  const httpStatus = queryError.data?.httpStatus || queryError.status
  const path = queryError.data?.path || queryError.path

  // In development, show verbose error details
  if (import.meta.env.DEV) {
    const verboseMessage = `
🔴 Backend Error
━━━━━━━━━━━━━━━━━━━━━
📍 ${path || "Unknown endpoint"}
⚠️ ${errorCode}${httpStatus ? ` (${httpStatus})` : ""}
💬 ${errorMessage}
━━━━━━━━━━━━━━━━━━━━━
    `.trim()

    toast.error(verboseMessage, {
      duration: 8000,
      style: {
        background: "#1f2937",
        color: "#fff",
        fontSize: "12px",
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        maxWidth: "500px",
      },
    })

    // Also log to console for debugging
    console.error("[tRPC Error]", {
      path,
      code: errorCode,
      httpStatus,
      message: errorMessage,
      fullError: error,
    })
  } else {
    // In production, show user-friendly message
    toast.error(errorMessage, {
      duration: 5000,
    })
  }
}

const isCacheDebugEnabled = () =>
  import.meta.env.DEV && typeof window !== "undefined" && window.localStorage.getItem("aix:cache-debug") === "1"

/**
 * Create React Query client with sensible defaults and global error handler
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Increase stale time for better UX
      staleTime: 60 * 1000, // 1 minute
      // Cache time for inactive queries (15 minutes)
      cacheTime: 15 * 1000 * 60, // Every 15 minutes inactive queries are garbage collected
      // Retry failed requests
      retry: (failureCount, error: unknown) => {
        const queryError = asQueryErrorLike(error)
        // Don't retry 4xx errors (client errors)
        if (queryError.data?.httpStatus && queryError.data.httpStatus >= 400 && queryError.data.httpStatus < 500) {
          return false
        }
        return failureCount < 3
      },
      // Disable refetch on window focus for better UX with cache
      refetchOnWindowFocus: false,
      // Global error handler for queries
      onError: handleGlobalError,
    },
    mutations: {
      // Global error handler for mutations
      onError: handleGlobalError,
    },
  },
})

/**
 * Create localStorage persister for React Query
 * This caches query results to localStorage to survive page reloads
 */
const persister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
  key: `aix-query-cache-${CACHE_VERSION}`,
  // Same transformer as tRPC, so restored Dates are Dates rather than strings
  serialize: superjson.stringify,
  deserialize: superjson.parse,
})

/** Cached queries outlive a reload by up to MAX_CACHE_AGE (personal queue, revenue), so sign-out drops them from
 *  memory and localStorage before the next person signs in on this browser */
const clearQueryCache = () => {
  queryClient.clear()
  persister.removeClient()
}

/**
 * Create tRPC client - URL is determined automatically
 */
const trpcClient = createTRPCReactClient()

/**
 * Main App Component with Providers
 */
const AppWithProviders: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider onSignedOut={clearQueryCache}>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister,
            maxAge: MAX_CACHE_AGE,
            dehydrateOptions: {
              // BLACKLIST APPROACH: Persist everything EXCEPT queries in SKIP_PERSIST_KEYS
              shouldDehydrateQuery: (query) => {
                // tRPC query keys are arrays like: [["router", "procedure"], {input}]
                // Extract the query key string from the first element
                const firstKey = query.queryKey[0]
                const queryKeyString = Array.isArray(firstKey)
                  ? firstKey.map((key) => stringifyQueryKeyPart(key)).join(".")
                  : stringifyQueryKeyPart(firstKey)

                // Skip if it matches any blacklist patterns
                const shouldSkip = SKIP_PERSIST_KEYS.some((skipKey) => queryKeyString.includes(skipKey))
                if (shouldSkip) {
                  if (isCacheDebugEnabled()) {
                    console.debug(`[Cache] ❌ Skipped (blacklisted): ${queryKeyString}`)
                  }
                  return false
                }

                // Check if query has custom expiry time
                const customExpiry = QUERY_CACHE_EXPIRY[queryKeyString]
                if (customExpiry) {
                  const queryAge = Date.now() - (query.state.dataUpdatedAt || 0)
                  const isExpired = queryAge > customExpiry

                  if (isExpired) {
                    if (isCacheDebugEnabled()) {
                      console.debug(`[Cache] ⏰ Expired (${queryAge}ms > ${customExpiry}ms): ${queryKeyString}`)
                    }
                    return false
                  }
                }

                // Persist everything else!
                if (isCacheDebugEnabled()) {
                  console.debug(`[Cache] ✅ Persisting: ${queryKeyString}`)
                }
                return true
              },
            },
          }}
        >
          <trpc.Provider client={trpcClient} queryClient={queryClient}>
            <App />
            <ReactQueryDevtools initialIsOpen={false} />
          </trpc.Provider>
        </PersistQueryClientProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

/**
 * Render the app
 */
const rootElement = document.getElementById("root")
if (!rootElement) {
  throw new Error("Root element not found")
}

const root = ReactDOM.createRoot(rootElement)
root.render(
  <React.StrictMode>
    <AppWithProviders />
  </React.StrictMode>,
)
